import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useContext, useState } from 'react';
import { Col, Container, Modal, Row } from 'react-bootstrap';
import Accordion from '../../../components/Accordion';
import AccordionBodyItem from '../../../components/Accordion/AccordionBodyItem';
import AvatarGroup, {
  AvatarGroupSize,
  AvatarGroupStyle,
} from '../../../components/Avatar/AvatarGroup';
import Button from '../../../components/Button';
import IconButton from '../../../components/IconButton';
import Label from '../../../components/Label';
import Popover from '../../../components/Popover';
import useMobile from '../../../hooks/useMobileHook';
import { AuthStore } from '../../../providers/auth';
import { EnrollmentStore } from '../../../providers/enrollment';
import {
  addOrRemoveDaysToGivenDate,
  formatDate,
  formatPrice,
} from '../../../utils/utils';
import { useNavigate } from 'react-router-dom';


const separateStringAndMakeFirstLetterUppercase = (str: string) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCardIcon = (type: String) => {
  switch (type) {
    case 'heart-pulse':
      return light('heart-pulse');
    case 'tooth':
      return light('tooth');
    case 'glasses':
      return light('glasses');
    case 'face-thermometer':
      return light('face-thermometer');
    case 'family':
      return light('family');
    case 'bed-pulse':
      return light('bed-pulse');
    case 'hospitals':
      return light('hospitals');
    case 'gavel':
      return light('gavel');
    case 'clipboard-medical':
      return light('clipboard-medical');
    default:
      return light('image-slash');
  }
};

const getPlanCostStyle = (type: string) => {
  switch (type) {
    case 'owed':
      return 'default';
    case 'credit':
      return 'green';
    default:
      return 'disabled';
  }
};

const getButtonStyle = (alertInfo: any) => {
  if (!alertInfo) {
    return;
  }

  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
      <Col className="m-0 d-flex justify-content-end">
        {alertInfo && alertInfo[0].ctaLabel ? (
          <Button size="sm" variant="primary">
            {alertInfo && alertInfo[0].ctaLabel}
          </Button>
        ) : (
          ''
        )}
      </Col>
    </Col>
  );

  return (
    <Popover placement="top-end" popoverBody={alertBody}>
      <IconButton
        variant="icon-alert"
        icon={light('circle-exclamation')}
        size={'sm'}
      />
    </Popover>
  );
};

const getAlertButton = (alertInfo: any[]) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
    </Col>
  );

  return (
    <Popover placement="top-end" popoverBody={alertBody}>
      <IconButton
        variant="icon-alert"
        icon={light('circle-exclamation')}
        size={'sm'}
      />
    </Popover>
  );
};





export type BenefitElectionsModalProps = React.PropsWithChildren<{
  showBenefitElectionsModal: boolean;
  handleCloseBenefitElectionsModal: () => void;
}>;

const BenefitElectionModal = ({
  showBenefitElectionsModal,
  handleCloseBenefitElectionsModal,
}: BenefitElectionsModalProps) => {
  const isMobile = useMobile();
  const [, ,] = useContext(AuthStore) || [];
  const {
    currentCost,
    updatedCost,
    activeHealthBenefits,
    activeLifeAndDisabilityInsurances,
    activeLifestyleBenefits,
    activeSpendingAccounts,
    medicalSelectedProfiles,
    dentalSelectedProfiles,
    criticalIlnessSelectedProfiles,
    accidentInsuranceSelectedProfiles,
    visionSelectedProfiles,
  } = useContext(EnrollmentStore) || {};
  const navigate = useNavigate();

  const medicalPlan = activeHealthBenefits.find(
    (e: any) => e.type === 'medical'
  );

  const healthBenefits = activeHealthBenefits.filter(
    (e: any) => !(medicalPlan?.plan.id !== 'med-hdhp' && e.type === 'health-savings')
  );

  const [shouldShowCurrentValue, setShowCurrentValue] =
    useState<boolean>(false);

  const getActiveProfilesByType = (type: string) => {
    switch (type) {
      case 'medical':
        return medicalSelectedProfiles.flat();
      case 'vision':
        return visionSelectedProfiles.flat();
      case 'dental':
        return dentalSelectedProfiles.flat();
      case 'critical-illness':
        return criticalIlnessSelectedProfiles.flat();
      case 'accident-insurance':
        return accidentInsuranceSelectedProfiles.flat();
    }
  };

  const getSubtitleAlertButton = (alertInfo: any[], proceedBtnFunc: any) => {
    // Get the current year as a string
    const currentYear = new Date().getFullYear().toString();

    // Regular expression to match years (1900-2099)
    const yearRegex = /\b(19|20)\d{2}\b/g;

    const alertBody = (
      <Col>
        <Row className="pb-7 m-0 fw-normal pt-2">
          {alertInfo && alertInfo[1].title}
        </Row>
        <Row className="pb-2 m-0 fw-light lh-xs">
          {alertInfo && alertInfo[1].description.replace(yearRegex, currentYear)}
        </Row>
        <Col className="m-0 mt-3 d-flex justify-content-end">
          <Button size={'sm'} onClick={() => {navigate('/payment-information/pending-pay')}}>
            Make Payment
          </Button>
        </Col>
      </Col>
    );
    return (
      <Popover placement="top-end" popoverBody={alertBody}>
        <IconButton
          variant="icon-alert"
          icon={light('circle-exclamation')}
          size={'sm'}
          extLabel="Pending COBRA"
          styleColor={'#f93b1d'}
        />
      </Popover>
    );
  };

  return (
    <Modal
      fullscreen={isMobile}
      size={'xl'}
      show={showBenefitElectionsModal}
      onHide={handleCloseBenefitElectionsModal}
    >
      <Modal.Header closeButton>
        <h2 className={'text-sm-20-lg-32 fw-light'}>Benefit Summary</h2>
      </Modal.Header>
      <Modal.Body>
        <Container className="cnd-custom-container mb-12 px-lg-9 px-xl-0">
          <Row className={'align-items-center mb-10 mb-lg-13'}>
            <Col className={'col-6 mt-10 d-flex flex-column'}>
              <span className={'text-dark-grey text-sub1 fw-light mb-3'}>
                Effective Date
              </span>
              <span className={'text-dark-grey text-base fw-normal'}>
                {formatDate(addOrRemoveDaysToGivenDate(new Date(), 30))}
              </span>
            </Col>
            <Col className={'col-6 mt-10 d-flex flex-column align-items-end'}>
              <Label
                className={'p-0 mb-3 text-end'}
                rightIcon={light('circle-exclamation')}
              >
                Total cost
              </Label>
              <span className={'text-blue fw-light mb-3 text-fs-5'}>
                {formatPrice(updatedCost)}
              </span>
              <span className={'text-sub1 text-dark-grey fw-light'}>
                per-pay-period
              </span>
            </Col>
          </Row>

          <Row className="mt-10">
            <Accordion title={'Health Benefits'}>
              <Container className="custom-accordion-demo heading">
                <Row className="body-rows">
                  <Col className="title-container col-lg-1">&nbsp;</Col>
                  <Col className="avatars-col col-lg-3">&nbsp;</Col>
                  <Col className="avatars-col col-lg-2">&nbsp;</Col>
                  <Col className="d-flex p-lg-0 col-lg-2">
                    <Col className="price-col me-6 me-lg-0">
                      <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                        {shouldShowCurrentValue ? 'Current Cost' : ''}
                      </span>
                    </Col>
                  </Col>
                </Row>
              </Container>
              {healthBenefits.map((benefit: any, index: number) => (
                <AccordionBodyItem
                  key={index}
                  infoIcon={getCardIcon(
                    benefit['generic'] && benefit['generic']['icon']
                  )}
                  title={benefit['generic']['title']}
                  subtitle={
                    benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                  }
                  subsubtitle={
                    !benefit['plan']['id'].includes('Coverage') &&
                    getSubtitleAlertButton(
                      benefit['alerts'],
                      benefit['generic']['title']
                    )
                  }
                  price={
                    benefit['plan']
                      ? formatPrice(Math.abs(benefit['plan']['cost']))
                      : '$0.00'
                  }
                  isOriginalPriceShown={shouldShowCurrentValue}
                  priceStyleType={getPlanCostStyle(
                    benefit['plan']['cost'] === 0
                      ? ''
                      : benefit['plan'] && benefit['plan']['costType']
                  )}
                  isHint={benefit['generic']['hint']}
                  avatarGroup={
                    benefit['generic']['familyMembers'] && (
                      <AvatarGroup
                        key={index}
                        avatars={getActiveProfilesByType(benefit['type'])}
                        groupSize={AvatarGroupSize.MINIMIZED}
                        groupStyle={AvatarGroupStyle.STACKED}
                        hasTooltip
                      />
                    )
                  }
                />
              ))}
            </Accordion>
          </Row>

          <Row className="mt-10">
            <Accordion title={'Spending Accounts'}>
              <Container className="custom-accordion-demo heading">
                <Row className="body-rows">
                  <Col className="title-container col-lg-4">&nbsp;</Col>
                  <Col className="avatars-col col-lg-2">&nbsp;</Col>
                  <Col className="d-flex col-lg-3">
                    <Col className="price-col me-6 me-lg-0">
                      <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                        {shouldShowCurrentValue ? 'Current cost' : ''}
                      </span>
                    </Col>
                  </Col>
                </Row>
              </Container>
              {activeSpendingAccounts.map((benefit: any, index: number) => (
                <AccordionBodyItem
                  key={index}
                  infoIcon={getCardIcon(
                    benefit['generic'] && benefit['generic']['icon']
                  )}
                  infoIconVariant={'icon-default'}
                  title={
                    !benefit['generic']
                      ? separateStringAndMakeFirstLetterUppercase(
                          benefit['type']
                        )
                      : benefit['generic']['title']
                  }
                  subtitle={
                    benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                  }
                  subsubtitle={
                    !benefit['plan']['id'].includes('Coverage') &&
                    getSubtitleAlertButton(
                      benefit['alerts'],
                      benefit['generic']['title']
                    )
                  }
                  price={
                    benefit['plan']
                      ? formatPrice(benefit['plan']['cost'])
                      : '$0.00'
                  }
                  infoButton={
                    !benefit['plan']['title'] ||
                    (benefit['plan']['title']?.includes('Coverage') &&
                      getButtonStyle(benefit['alerts'] && benefit['alerts']))
                  }
                  isOriginalPriceShown={shouldShowCurrentValue}
                  priceStyleType={getPlanCostStyle(benefit['plan']['costType'])}
                />
              ))}
            </Accordion>
          </Row>
          {activeLifeAndDisabilityInsurances.length > 0 && (
            <Row className="mt-10">
              <Accordion
                title={'Life & Disability Insurance'}
                infoIcon={light('circle-exclamation')}
              >
                <Container className="custom-accordion-demo heading">
                  <Row className="body-rows">
                    <Col className="title-container col-lg-4">&nbsp;</Col>
                    <Col className="avatars-col col-lg-2">&nbsp;</Col>
                    <Col className="d-flex col-lg-3">
                      <Col className="price-col me-6 me-lg-0">
                        <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                          {shouldShowCurrentValue ? 'Current cost' : ''}
                        </span>
                      </Col>
                    </Col>
                  </Row>
                </Container>
                {activeLifeAndDisabilityInsurances.map(
                  (benefit: any, index: number) => {
                    console.log(benefit['alerts']);
                    return (
                      <AccordionBodyItem
                        key={index}
                        infoIcon={getCardIcon(
                          benefit['generic'] && benefit['generic']['icon']
                        )}
                        title={
                          !benefit['generic']
                            ? separateStringAndMakeFirstLetterUppercase(
                                benefit['type']
                              )
                            : benefit['generic']['name']
                        }
                        infoIconVariant={'icon-default'}
                        subtitle={
                          !benefit['plan']['id'].includes('noCoverage')
                            ? `${benefit['plan']['title']}${
                                benefit['plan']['coverage']
                                  ? `, ${formatPrice(
                                      benefit['plan']['coverage'],
                                      'en-US',
                                      {
                                        currency: 'USD',
                                        style: 'currency',
                                        minimumFractionDigits: 0,
                                      }
                                    )}`
                                  : ''
                              }`
                            : 'No Coverage'
                        }
                        price={
                          benefit['plan']
                            ? formatPrice(benefit['plan']['cost'])
                            : '$0.00'
                        }
                        infoButton={
                          benefit['alerts'] && getAlertButton(benefit['alerts'])
                        }
                        isOriginalPriceShown={shouldShowCurrentValue}
                        priceStyleType={getPlanCostStyle(
                          benefit['plan'] && benefit['plan']['costType']
                        )}
                      />
                    );
                  }
                )}
              </Accordion>
            </Row>
          )}

          {activeLifestyleBenefits.length > 0 && (
            <Row className="mt-10">
              <Accordion title={'Lifestyle Benefits'}>
                <Container className="custom-accordion-demo heading">
                  <Row className="body-rows">
                    <Col className="title-container col-lg-4">&nbsp;</Col>
                    <Col className="avatars-col col-lg-2">&nbsp;</Col>
                    <Col className="d-flex col-lg-3">
                      <Col className="price-col me-6 me-lg-0">
                        <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                          {shouldShowCurrentValue ? 'Current cost' : ''}
                        </span>
                      </Col>
                    </Col>
                  </Row>
                </Container>
                {activeLifestyleBenefits.map((benefit: any, index: number) => (
                  <AccordionBodyItem
                    key={index}
                    infoIcon={getCardIcon(
                      benefit['generic'] && benefit['generic']['icon']
                    )}
                    title={
                      !benefit['generic']
                        ? separateStringAndMakeFirstLetterUppercase(
                            benefit['type']
                          )
                        : benefit['generic']['title']
                    }
                    infoIconVariant={'icon-default'}
                    subtitle={
                      benefit['plan'] ? benefit['plan']['title'] : 'No Coverage'
                    }
                    price={
                      benefit['plan']
                        ? formatPrice(benefit['plan']['cost'])
                        : '$0.00'
                    }
                    infoButton={
                      !benefit['plan']['title'] ||
                      (benefit['plan']['title']?.includes('Coverage') &&
                        getButtonStyle(benefit['alerts'] && benefit['alerts']))
                    }
                    isOriginalPriceShown={shouldShowCurrentValue}
                    priceStyleType={getPlanCostStyle(
                      benefit['plan'] && benefit['plan']['costType']
                    )}
                  />
                ))}
              </Accordion>
            </Row>
          )}

          <section className="mt-10 d-flex flex-column flex-lg-row justify-content-center">
            <Button
              onClick={handleCloseBenefitElectionsModal}
              className="text-nowrap order-2 order-lg-1 mt-7 mt-lg-0 me-lg-7"
              variant="primary-outline"
            >
              Close
            </Button>
          </section>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default BenefitElectionModal;
